export interface User {
  id: string
  username: string
  email: string
  first_name?: string
  last_name?: string
  is_admin: boolean
  roles: string[]
}

class AuthService {
  private user: User | null = null
  private baseUrl = window.location.hostname === 'shop.nickfixit.com'
    ? 'https://api.nickfixit.com/api'
    : (import.meta.env.VITE_API_BASE_URL
      ? `${import.meta.env.VITE_API_BASE_URL}/api`
      : `${window.location.protocol}//${window.location.hostname}:8000/api`)

  constructor() {
    // No token storage in localStorage - using HTTP-only cookies
  }

  async initialize(): Promise<boolean> {
    try {
      // Check authentication status with the backend
      const response = await fetch(`${this.baseUrl}/auth/status`, {
        credentials: 'include', // Include cookies
      })

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated) {
          await this.loadUserProfile()
          return true
        }
      }

      return false
    } catch (error) {
      console.error('Failed to check auth status:', error)
      return false
    }
  }

  async login(): Promise<void> {
    // Redirect to OAuth2 login endpoint
    const currentUrl = window.location.href
    window.location.href = `${this.baseUrl}/auth/login?redirect_url=${encodeURIComponent(currentUrl)}`
  }

  async logout(): Promise<void> {
    try {
      // Clear local state first
      this.user = null

      console.log('Logout: Making POST request to:', `${this.baseUrl}/auth/logout`)

      // Make a POST request to logout endpoint
      const response = await fetch(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      console.log('Logout response status:', response.status)
      console.log('Logout response redirected:', response.redirected)
      console.log('Logout response url:', response.url)

      // Check if response is a redirect (302)
      if (response.status === 302 || response.redirected) {
        // Follow the redirect to Keycloak logout
        if (response.url) {
          window.location.href = response.url
        } else {
          // No redirect URL, just go to home
          window.location.href = '/'
        }
      } else {
        // If not a redirect, try to get the redirect URL from response
        try {
          const data = await response.text()
          // If response contains a redirect URL, use it
          if (data.includes('https://auth.elitebench.com')) {
            // Extract the Keycloak logout URL from the response
            const urlMatch = data.match(/https:\/\/auth\.elitebench\.com[^"'\s]*/);
            if (urlMatch) {
              window.location.href = urlMatch[0]
              return
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }

        // Fallback: redirect to home
        window.location.href = '/'
      }
    } catch (error) {
      console.error('Logout failed:', error)
      // Fallback: clear local state and redirect to home
      this.user = null
      window.location.href = '/'
    }
  }

  async getToken(): Promise<string | undefined> {
    // Tokens are in HTTP-only cookies, not accessible to JavaScript
    // Return undefined to indicate cookie-based auth
    return undefined
  }

  isAuthenticated(): boolean {
    return this.user !== null
  }

  getUser(): User | null {
    return this.user
  }

  hasRole(role: string): boolean {
    if (!this.user) {
      return false
    }
    return this.user.roles.includes(role)
  }

  isAdmin(): boolean {
    return this.user?.is_admin || false
  }

  private async loadUserProfile(): Promise<void> {
    try {
      // Use the new user endpoint that gets info from cookies
      const response = await fetch(`${this.baseUrl}/auth/user`, {
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to load user profile: ${response.status}`)
      }

      this.user = await response.json()
    } catch (error) {
      console.error('Failed to load user profile:', error)
      throw error
    }
  }

  // Get user info for display
  getUserInfo() {
    if (!this.user) {
      return null
    }

    return {
      id: this.user.id,
      username: this.user.username,
      email: this.user.email,
      name: this.user.first_name && this.user.last_name
        ? `${this.user.first_name} ${this.user.last_name}`
        : this.user.username,
      roles: this.user.roles,
      isAdmin: this.user.is_admin,
    }
  }

  // Get authorization header for API calls
  getAuthHeader() {
    // No authorization header needed - using HTTP-only cookies
    return {}
  }
}

// Export singleton instance
export const authService = new AuthService()